import type { EmailDeliverySummary } from '@/types/email-delivery';

export const EMAIL_DELIVERY_POLL_INTERVAL_MS = 4_000;
/** Poll a queued email this fast for this long; after it the badge says Retrying. */
export const EMAIL_DELIVERY_POLL_WINDOW_MS = 3 * 60_000;
/**
 * Past the window a queued email is still being retried (about 7.5 min of
 * backoff, then the sweeper), so keep refreshing, just slower, until it settles.
 */
export const EMAIL_DELIVERY_SLOW_POLL_INTERVAL_MS = 30_000;

export type EmailDeliveryDisplayState =
  | 'sending'
  | 'retrying'
  | 'sent'
  | 'logged'
  | 'failed'
  | 'not_sent'
  | 'canceled'
  | 'delivered'
  | 'bounced'
  | 'dropped';

export const EMAIL_DELIVERY_LABELS: Record<EmailDeliveryDisplayState, string> = {
  sending: 'Sending…',
  retrying: 'Retrying',
  sent: 'Sent',
  logged: 'Logged (not delivered)',
  failed: 'Failed',
  not_sent: 'Not sent',
  canceled: 'Canceled',
  delivered: 'Delivered',
  bounced: 'Bounced',
  dropped: 'Dropped',
};

/** Badge colour per display state, shared by every surface that shows one. */
export const EMAIL_DELIVERY_BADGE_VARIANTS: Record<
  EmailDeliveryDisplayState,
  'secondary' | 'destructive' | 'success' | 'warning' | 'info' | 'outline'
> = {
  sending: 'info',
  retrying: 'warning',
  sent: 'success',
  logged: 'warning',
  failed: 'destructive',
  not_sent: 'secondary',
  canceled: 'outline',
  delivered: 'success',
  bounced: 'destructive',
  dropped: 'destructive',
};

function queuedFor(delivery: EmailDeliverySummary, nowMs: number): number {
  const queuedAt = delivery.queuedAt ? Date.parse(delivery.queuedAt) : NaN;
  return Number.isNaN(queuedAt) ? 0 : nowMs - queuedAt;
}

export function getEmailDeliveryDisplayState(
  delivery: EmailDeliverySummary,
  nowMs: number = Date.now(),
): EmailDeliveryDisplayState {
  switch (delivery.status) {
    case 'QUEUED':
      // `attempts` is incremented before the send, so 1 means the first attempt
      // is in flight; only a second attempt proves one failed.
      return delivery.attempts > 1 || queuedFor(delivery, nowMs) >= EMAIL_DELIVERY_POLL_WINDOW_MS
        ? 'retrying'
        : 'sending';
    case 'SENT':
      return delivery.simulated ? 'logged' : 'sent';
    case 'FAILED':
      return 'failed';
    case 'NOT_SENT':
      return 'not_sent';
    case 'CANCELED':
      return 'canceled';
    case 'DELIVERED':
      return 'delivered';
    case 'BOUNCED':
      return 'bounced';
    case 'DROPPED':
      return 'dropped';
  }
}

/**
 * For TanStack `refetchInterval`: 4 s while queued inside the 3-minute window,
 * 30 s for as long as it stays queued after that, and no polling once settled.
 */
export function getEmailDeliveryPollInterval(
  delivery: EmailDeliverySummary | null | undefined,
  nowMs: number = Date.now(),
): number | false {
  if (!delivery || delivery.status !== 'QUEUED') return false;
  return queuedFor(delivery, nowMs) < EMAIL_DELIVERY_POLL_WINDOW_MS
    ? EMAIL_DELIVERY_POLL_INTERVAL_MS
    : EMAIL_DELIVERY_SLOW_POLL_INTERVAL_MS;
}

/** The fastest of several `refetchInterval` answers; `false` when none polls. */
export function fastestPollInterval(...intervals: Array<number | false>): number | false {
  let fastest: number | false = false;
  for (const interval of intervals) {
    if (interval !== false && (fastest === false || interval < fastest)) fastest = interval;
  }
  return fastest;
}

/**
 * Ms until a Sending… badge turns Retrying because the polling window closes,
 * or null when that cannot happen (not queued, or already Retrying).
 */
export function msUntilEmailDeliveryRetrying(
  delivery: EmailDeliverySummary,
  nowMs: number = Date.now(),
): number | null {
  if (getEmailDeliveryDisplayState(delivery, nowMs) !== 'sending') return null;
  return Math.max(0, EMAIL_DELIVERY_POLL_WINDOW_MS - queuedFor(delivery, nowMs));
}

/** "Fix email & resend" is the primary action on Failed and Not sent: no email. */
export function canFixReceiptEmail(delivery: EmailDeliverySummary | null | undefined): boolean {
  if (!delivery) return false;
  return (
    delivery.status === 'FAILED' ||
    (delivery.status === 'NOT_SENT' && delivery.errorCode === 'no_email')
  );
}
